import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AppLayout from '../../components/AppLayout';
import { getOperationsIntelligence } from '../../services/intelligenceService';

const RefreshIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M21 12a9 9 0 0 1-15.5 6.2" />
    <path d="M3 12A9 9 0 0 1 18.5 5.8" />
    <path d="M18 2v4h4" />
    <path d="M6 22v-4H2" />
  </svg>
);

const severityClass = (severity) => {
  switch (severity) {
    case 'critical':
    case 'high':
      return 'badge-warning';
    case 'medium':
      return 'badge-info';
    case 'low':
    case 'info':
    default:
      return 'badge-success';
  }
};

const postureClass = (posture) => {
  if (posture === 'critical') return 'intel-posture--critical';
  if (posture === 'intervention') return 'intel-posture--intervention';
  if (posture === 'watch') return 'intel-posture--watch';
  return 'intel-posture--clear';
};

const scoreClass = (score) => {
  if (score >= 85) return 'intel-score-good';
  if (score >= 65) return 'intel-score-watch';
  return 'intel-score-risk';
};

const postureLabel = (posture) => {
  if (posture === 'critical') return 'Critical';
  if (posture === 'intervention') return 'Intervention needed';
  if (posture === 'watch') return 'Watch';
  return 'Clear';
};

const statusLabel = (status) => {
  if (!status) return 'unknown';
  return String(status).replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim();
};

const formatNumber = (value) => Number(value || 0).toLocaleString();

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  });

const formatPercent = (value) => {
  if (value === null || value === undefined) return '-';
  return `${Math.round(Number(value || 0) * 100)}%`;
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const EmptyState = ({ children }) => (
  <div className="empty-state">{children}</div>
);

const SectionHeader = ({ title, action }) => (
  <div className="panel-header">
    <h3>{title}</h3>
    {action}
  </div>
);

const StatCell = ({ label, value, hint, tone }) => (
  <div className={`intel-stat-cell ${tone ? `intel-stat-cell--${tone}` : ''}`}>
    <span>{label}</span>
    <strong>{value}</strong>
    {hint ? <em>{hint}</em> : null}
  </div>
);

const ScoreTile = ({ label, score, subline }) => (
  <div className={`intel-score-tile ${scoreClass(score)}`}>
    <div>
      <span>{label}</span>
      <strong>{Math.round(Number(score || 0))}</strong>
    </div>
    {subline && <p>{subline}</p>}
  </div>
);

const OperatingPosture = ({ data }) => {
  const scores = data?.scores || {};
  const metrics = data?.metrics || {};
  const activeSummary = data?.activeSummary || {};

  return (
    <div className={`intel-command-panel ${postureClass(data?.posture)}`}>
      <div className="intel-command-main">
        <div className="intel-kicker">Live operating posture</div>
        <h2>{postureLabel(data?.posture)}</h2>
        <p>{data?.headline || 'Operations summary generated.'}</p>
        <div className="intel-command-meta">
          <span>Generated {formatDateTime(data?.generatedAt)}</span>
          <span>Next {data?.config?.windowHours || 12} hours</span>
          <span>{data?.engine?.name || 'TaxiOps intelligence'}</span>
        </div>
      </div>

      <div className="intel-score-grid">
        <ScoreTile label="Overall" score={scores.overall ?? data?.score} subline={data?.riskLevel || 'unknown'} />
        <ScoreTile label="Dispatch" score={scores.dispatch} subline={`${formatNumber(metrics.upcomingNonFinal)} upcoming`} />
        <ScoreTile label="Fleet" score={scores.fleet} subline={`${formatNumber(activeSummary.onlineActive)} online active`} />
      </div>
    </div>
  );
};

const AttentionQueue = ({ items = [] }) => (
  <div className="panel intel-attention-panel">
    <SectionHeader
      title="Priority attention queue"
      action={<span className="intel-count-pill">{formatNumber(items.length)} signal{items.length === 1 ? '' : 's'}</span>}
    />
    {!items.length ? (
      <EmptyState>No attention items were returned.</EmptyState>
    ) : (
      <div className="intel-attention-list">
        {items.map((item, index) => (
          <div key={item.id || item.title} className={`intel-attention-item intel-attention-item--${item.severity || 'info'}`}>
            <div className="intel-attention-rank">{index + 1}</div>
            <div className="intel-attention-copy">
              <div className="intel-row-heading">
                <h4>{item.title}</h4>
                <span className={`badge ${severityClass(item.severity)}`}>{item.severity || 'info'}</span>
              </div>
              <p>{item.detail}</p>
              {item.impact ? <div className="intel-impact">{item.impact}</div> : null}
              {item.records?.length ? (
                <div className="intel-record-list">
                  {item.records.map((record) => (
                    <Link key={record.id || record.label} to={record.route || item.route || '#'} className="intel-record-chip">
                      <strong>{record.label}</strong>
                      {record.meta ? <span>{record.meta}</span> : null}
                    </Link>
                  ))}
                </div>
              ) : null}
            </div>
            {item.route ? (
              <Link to={item.route} className="btn btn-subtle">
                {item.actionLabel || 'Open'}
              </Link>
            ) : null}
          </div>
        ))}
      </div>
    )}
  </div>
);

const DispatchReadiness = ({ readiness = {} }) => {
  const rows = readiness.exceptionTrips?.length ? readiness.exceptionTrips : readiness.upcomingPreview || [];

  return (
    <div className="panel">
      <SectionHeader
        title="Dispatch readiness"
        action={<Link to="/bookings" className="btn btn-subtle">Bookings</Link>}
      />
      <div className="intel-stat-grid">
        <StatCell label="Upcoming" value={formatNumber(readiness.upcomingTotal)} hint={`next ${readiness.windowHours || 12}h`} />
        <StatCell label="Unassigned" value={formatNumber(readiness.unassignedUpcoming)} tone={readiness.unassignedUpcoming ? 'warn' : 'good'} />
        <StatCell label="Reassign" value={formatNumber(readiness.needsReassignment)} tone={readiness.needsReassignment ? 'warn' : 'good'} />
        <StatCell label="Coverage" value={formatPercent(readiness.coverageRatio)} hint={`${formatNumber(readiness.onlineActiveDrivers)} online`} />
      </div>

      {!rows.length ? (
        <EmptyState>No upcoming non-final pickups in the selected window.</EmptyState>
      ) : (
        <div className="intel-table-list">
          {rows.map((booking) => (
            <Link key={booking.id} to={booking.route} className="intel-table-row">
              <div>
                <strong>{formatDateTime(booking.pickupTime)}</strong>
                <span>{booking.customerName || 'Customer pending'}</span>
                <small>{booking.pickupAddress || 'Pickup address pending'}</small>
              </div>
              <div className="intel-row-badges">
                {booking.overdue ? <span className="badge badge-warning">overdue</span> : null}
                {booking.needsReassignment ? <span className="badge badge-warning">reassign</span> : null}
                {booking.unassigned ? <span className="badge badge-info">unassigned</span> : null}
                {!booking.overdue && !booking.needsReassignment && !booking.unassigned ? (
                  <span className="badge badge-success">{booking.status || 'scheduled'}</span>
                ) : null}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const FleetReadiness = ({ readiness = {} }) => (
  <div className="panel">
    <SectionHeader
      title="Fleet readiness"
      action={<Link to="/operations/dispatch" className="btn btn-subtle">Map</Link>}
    />
    <div className="intel-stat-grid">
      <StatCell label="Active" value={formatNumber(readiness.active)} />
      <StatCell label="Online" value={formatNumber(readiness.onlineActive)} />
      <StatCell label="Stale" value={formatNumber(readiness.staleLocations)} tone={readiness.staleLocations ? 'warn' : 'good'} />
      <StatCell label="HOS flags" value={formatNumber((readiness.hosWarnings || 0) + (readiness.hosViolations || 0))} tone={(readiness.hosWarnings || readiness.hosViolations) ? 'warn' : 'good'} />
    </div>

    {!readiness.watchList?.length ? (
      <EmptyState>No active fleet watch items right now.</EmptyState>
    ) : (
      <div className="intel-table-list">
        {readiness.watchList.map((row) => (
          <Link key={row.id || `${row.driverId}-${row.cabNumber}`} to={row.route} className="intel-table-row">
            <div>
              <strong>{row.driverName}</strong>
              <span>Cab {row.cabNumber || '-'}</span>
              <small>{row.reasons?.join(' - ') || 'Review roster status'}</small>
            </div>
            <span className={`badge ${row.hos?.overLimit ? 'badge-warning' : 'badge-info'}`}>
              {row.hos?.overLimit ? 'HOS' : statusLabel(row.availability)}
            </span>
          </Link>
        ))}
      </div>
    )}
  </div>
);

const DemandBarProfile = ({ profile, type }) => {
  const items = type === 'day' ? profile?.days : profile?.hours;
  const noun = type === 'day' ? 'daily demand averages' : 'hourly demand averages';

  if (!items?.length || profile.status === 'insufficient_data') {
    return <EmptyState>{noun.charAt(0).toUpperCase() + noun.slice(1)} will appear once booking history is available.</EmptyState>;
  }

  const title = type === 'day' ? 'Average demand by day' : 'Average demand by hour';
  const peakText = type === 'day'
    ? `Peak ${profile.peakLabel} at ${profile.peakAverage} pickups/day`
    : `Peak ${profile.peakLabel} at ${profile.peakAverage} pickups/hr`;

  return (
    <div className="intel-demand-profile">
      <div className="intel-demand-summary">
        <span>{title}</span>
        <strong>{peakText}</strong>
        <em>{formatNumber(profile.observedDays)} observed day{profile.observedDays === 1 ? '' : 's'}</em>
      </div>
      <div className={type === 'day' ? 'intel-day-chart' : 'intel-hourly-chart'}>
        {items.map((item) => (
          <div
            key={type === 'day' ? item.day : item.hour}
            className={[
              type === 'day' ? 'intel-day-row' : 'intel-hourly-row',
              item.isPeak ? `${type === 'day' ? 'intel-day-row' : 'intel-hourly-row'}--peak` : '',
              item.isCurrent ? `${type === 'day' ? 'intel-day-row' : 'intel-hourly-row'}--current` : '',
            ].filter(Boolean).join(' ')}
          >
            <span>{item.label}</span>
            <div><i style={{ width: `${Math.max(4, item.widthPct)}%` }} /></div>
            <strong>{item.average}</strong>
          </div>
        ))}
      </div>
      {profile.status === 'warming_up' ? (
        <p className="intel-panel-note">This profile is still warming up. It becomes more reliable as more booking history is recorded.</p>
      ) : null}
    </div>
  );
};

const PickupHotspots = ({ profile }) => {
  if (!profile?.locations?.length || profile.status === 'insufficient_data') {
    return <EmptyState>Hot pickup locations will appear once pickup history is available.</EmptyState>;
  }

  return (
    <div className="intel-hotspots">
      <div className="intel-demand-summary">
        <span>Rolling {profile.windowDays}-day pickup origins</span>
        <em>{formatNumber(profile.observedDays)} observed day{profile.observedDays === 1 ? '' : 's'}</em>
      </div>
      <div className="intel-hotspot-list">
        {profile.locations.slice(0, 6).map((location) => (
          <div key={`${location.rank}-${location.address}`} className="intel-hotspot-row">
            <div className="intel-hotspot-rank">{location.rank}</div>
            <div className="intel-hotspot-main">
              <div className="intel-hotspot-heading">
                <strong>{location.address}</strong>
                <span>{formatNumber(location.pickupCount)} pickups</span>
              </div>
              <div className="intel-hotspot-bar"><i style={{ width: `${Math.max(4, location.widthPct)}%` }} /></div>
              <div className="intel-hotspot-meta">
                <span>{location.averagePerDay} per observed day</span>
                <span>Last {formatDateTime(location.lastPickupAt)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
      {profile.status === 'warming_up' ? (
        <p className="intel-panel-note">Hot pickup ranking is still warming up. It becomes more reliable as pickup history grows.</p>
      ) : null}
    </div>
  );
};

const DemandSignals = ({ demand = {} }) => (
  <div className="panel">
    <SectionHeader
      title="Demand intelligence"
      action={<span className="intel-count-pill">{formatNumber(demand.activeCount)} active</span>}
    />
    <div className="intel-subsection-title">Hottest pickup origins</div>
    <PickupHotspots profile={demand.pickupHotspots} />

    <div className="intel-subsection-title">Average demand</div>
    <div className="intel-demand-profile-grid">
      <DemandBarProfile type="day" profile={demand.dailyDemand} />
      <DemandBarProfile type="hour" profile={demand.hourlyDemand} />
    </div>

    <div className="intel-subsection-title">Active Pulse signals</div>
    {!demand.signals?.length ? (
      <EmptyState>No active TaxiOps Pulse demand signals right now.</EmptyState>
    ) : (
      <div className="intel-demand-list">
        {demand.signals.slice(0, 3).map((signal) => (
          <div key={signal.id} className="intel-demand-card">
            <div className="intel-row-heading">
              <h4>{signal.title}</h4>
              <span className={`badge ${signal.level === 'Critical' || signal.level === 'Hot' ? 'badge-warning' : 'badge-info'}`}>
                {signal.level}
              </span>
            </div>
            <p>{signal.message}</p>
            <div className="intel-demand-meta">
              <span>{signal.locationLabel || signal.locationType}</span>
              <span>{formatNumber(signal.pickupCount)} pickups</span>
              <span>{signal.radiusMiles} mi radius</span>
              <span>Expires {formatDateTime(signal.expiresAt)}</span>
            </div>
            <div className="intel-impact">{signal.recommendation}</div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const TrendEvidence = ({ evidence = {}, metrics = {} }) => {
  const cancelDelta = Number(evidence.signals?.cancelRateDelta || 0);
  const demandDelta = Number(evidence.signals?.demandVsAverage || 0);

  return (
    <div className="panel">
      <SectionHeader
        title="Trend evidence"
        action={<Link to="/reports/financials" className="btn btn-subtle">Financials</Link>}
      />
      <div className="intel-stat-grid">
        <StatCell label="Trips today" value={formatNumber(metrics.totalBookingsToday)} hint={`${formatNumber(evidence.baseline?.averageTrips)} avg`} />
        <StatCell label="Revenue" value={formatCurrency(metrics.revenueToday)} />
        <StatCell label="Cancel rate" value={formatPercent(metrics.cancellationRateToday)} hint={`${cancelDelta >= 0 ? '+' : ''}${Math.round(cancelDelta * 100)} pts`} tone={cancelDelta >= 0.15 ? 'warn' : ''} />
        <StatCell label="Demand delta" value={`${demandDelta >= 0 ? '+' : ''}${Math.round(demandDelta * 100)}%`} />
      </div>

      {!evidence.rows?.length ? (
        <EmptyState>Trend baseline will appear once recent trip data is available.</EmptyState>
      ) : (
        <div className="intel-trend-bars">
          {evidence.rows.map((row) => {
            const maxTrips = Math.max(...evidence.rows.map((item) => item.totalTrips || 0), 1);
            const width = Math.max(5, Math.round(((row.totalTrips || 0) / maxTrips) * 100));
            return (
              <div key={row.date} className="intel-trend-row">
                <span>{row.date}</span>
                <div><i style={{ width: `${width}%` }} /></div>
                <strong>{formatNumber(row.totalTrips)}</strong>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const SystemHealth = ({ health = {} }) => (
  <div className="panel">
    <SectionHeader
      title="System health"
      action={<Link to={health.route || '/reports/diagnostics'} className="btn btn-subtle">Diagnostics</Link>}
    />
    <div className="intel-stat-grid">
      <StatCell label="Errors" value={formatNumber(health.diagnostics24h?.error)} tone={health.diagnostics24h?.error ? 'warn' : 'good'} />
      <StatCell label="Warnings" value={formatNumber(health.diagnostics24h?.warn)} tone={health.diagnostics24h?.warn ? 'warn' : 'good'} />
      <StatCell label="Status" value={statusLabel(health.status)} />
      <StatCell label="Window" value="24h" />
    </div>
    <p className="intel-panel-note">
      Driver app diagnostics are treated as operational risk when they can affect meter, sync, location, or trip execution.
    </p>
  </div>
);

const IntelligenceDashboard = () => {
  const [windowHours, setWindowHours] = useState(12);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const loadDashboard = useCallback(async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError('');

    try {
      const response = await getOperationsIntelligence({ windowHours });
      setData(response.data || null);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load operations intelligence right now.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [windowHours]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const headerActions = useMemo(
    () => (
      <div className="action-stack">
        <select
          className="filter-select"
          value={windowHours}
          onChange={(event) => setWindowHours(Number(event.target.value))}
          aria-label="Upcoming work window"
        >
          <option value={6}>Next 6 hours</option>
          <option value={12}>Next 12 hours</option>
          <option value={24}>Next 24 hours</option>
          <option value={48}>Next 48 hours</option>
        </select>
        <button
          type="button"
          className="btn btn-primary btn-with-svg"
          onClick={() => loadDashboard({ silent: true })}
          disabled={loading || refreshing}
        >
          <RefreshIcon />
          {refreshing ? 'Refreshing' : 'Refresh'}
        </button>
      </div>
    ),
    [loadDashboard, loading, refreshing, windowHours],
  );

  const renderSkeleton = () => (
    <>
      <div className="intel-command-panel skeleton" style={{ minHeight: 230 }} />
      <div className="panel skeleton" style={{ minHeight: 320, marginTop: 24 }} />
      <div className="grid-two responsive-cards" style={{ marginTop: 24 }}>
        <div className="panel skeleton" style={{ minHeight: 260 }} />
        <div className="panel skeleton" style={{ minHeight: 260 }} />
      </div>
    </>
  );

  return (
    <AppLayout
      title="Operations intelligence"
      subtitle="A prioritized command center for dispatch, fleet readiness, demand, trend, and system health."
      actions={headerActions}
    >
      {error && <div className="feedback error">{error}</div>}
      {loading && !data ? renderSkeleton() : null}

      {!loading && data ? (
        <>
          <OperatingPosture data={data} />
          <AttentionQueue items={data.attentionQueue || []} />

          <div className="grid-two responsive-cards" style={{ marginTop: 24 }}>
            <DispatchReadiness readiness={data.dispatchReadiness} />
            <FleetReadiness readiness={data.fleetReadiness} />
          </div>

          <div style={{ marginTop: 24 }}>
            <DemandSignals demand={data.demandSignals} />
          </div>

          <div className="grid-two responsive-cards" style={{ marginTop: 24 }}>
            <TrendEvidence evidence={data.trendEvidence} metrics={data.metrics} />
            <SystemHealth health={data.systemHealth} />
          </div>
        </>
      ) : null}
    </AppLayout>
  );
};

export default IntelligenceDashboard;
